// src/MainLayout.js
import React, { useState } from 'react'; // (1. Import useState)
import { Outlet, Link, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import logo from './logo.png';
// (2. Import axios และ toast)
import axios from 'axios';
import toast from 'react-hot-toast';

const API_URL = 'https://rys-backend.onrender.com/api';

// (อัปเกรด) AuthButton
function AuthButton() {
  const navigate = useNavigate();
  // (3. ดึง getAuthHeaders มาด้วย)
  const { isAuthenticated, user, logout, getAuthHeaders } = useAuth();
  
  // (4. เพิ่ม State สำหรับ Modal)
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // (5. เพิ่ม Handler สำหรับ Modal)
  const handleSubmitResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error("รหัสผ่านใหม่ต้องมีอย่างน้อย 6 ตัวอักษร");
      return;
    }
    setIsLoading(true);
    try {
      const response = await axios.put(
        `${API_URL}/users/change-password`, // (เรียก API ใหม่)
        { password: newPassword }, 
        getAuthHeaders() // (ใช้ Token ของตัวเอง)
      );
      toast.success(response.data.msg);
      setIsModalOpen(false); // (ปิด Modal)
      setNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Reset failed');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <Link 
        to="/login" 
        className="bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition duration-300"
      >
        Login
      </Link>
    );
  }

  return (
    <>
      <div className="flex items-center space-x-4">
        
        {/* (ปุ่ม Admin Panel - เหมือนเดิม) */}
        {user && user.role === 'admin' && (
          <Link 
            to="/admin" 
            className="bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 transition duration-300"
          >
            Admin Panel
          </Link>
        )}

        {/* (ปุ่ม "เปลี่ยนรหัสผ่าน" - จะแสดงเฉพาะ "User ทั่วไป") */}
        
        {/* --- (สิ้นสุดส่วนที่เพิ่ม) --- */}

        {/* (ปุ่ม Logout - เหมือนเดิม) */}
        <button 
          onClick={handleLogout}
          className="bg-red-500 text-white py-2 px-4 rounded-md hover:bg-red-600 transition duration-300"
        >
          Logout
        </button>
      </div>

      {/* --- (7. เพิ่ม Modal UI) --- */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-white w-full max-w-lg p-6 rounded-lg shadow-xl m-4">
            <h2 className="text-2xl font-semibold mb-5 text-gray-800 border-b pb-3">
              เปลี่ยนรหัสผ่านของคุณ
            </h2>
            <p className="mb-4 text-lg">
              คุณกำลังจะตั้งรหัสผ่านใหม่สำหรับ: <strong className="text-blue-600">{user.username}</strong>
            </p>
            <form onSubmit={handleSubmitResetPassword}>
              <div>
                <label htmlFor="newPassword" className="block text-base font-medium text-gray-700">
                  รหัสผ่านใหม่ (อย่างน้อย 6 ตัวอักษร)
                </label>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  className="mt-1 block w-full px-4 py-3 text-lg border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-6">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  disabled={isLoading}
                  className="px-5 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-5 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {isLoading ? "กำลังบันทึก..." : "ยืนยัน"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* --- (สิ้นสุด Modal) --- */}
    </>
  );
}

// (MainLayout)
function MainLayout() {
  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      
      {/* --- Header --- */}
      <header className="bg-white shadow-md sticky top-0 z-40">
        <div className=" px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            
            <Link to="/" className="flex items-center">
              <img 
                src={logo} 
                alt="logo" 
                className="h-14 w-14 mr-4"
              />
              <div>
                <h1 className="text-xl font-bold text-blue-700 leading-tight">
                  Rayong Shipping Co., Ltd.
                </h1>
                <h3 className="text-2xl font-semibold text-gray-800 leading-tight">
                  ระบบคลังความรู้ด้านศุลกากรและภาษี
                </h3>
              </div>
            </Link>
            
            <AuthButton />
          </div>
        </div>
      </header>

      {/* --- Main (flex-grow) --- */}
      <main className="flex-grow">
        <Outlet />
      </main>

      {/* --- Footer (Copyright) --- */}
      <footer className="text-center p-4 mt-8 bg-blue-600 shadow-inner border-t border-gray-200">
        <p className="text-sm text-white">
          Copyright © {new Date().getFullYear()} Rayong Shipping Co., Ltd. All rights reserved.
        </p>
      </footer>

    </div>
  );
}

export default MainLayout;